import { createTheme } from "@mui/material/styles";

// Single source of truth for the site's warm palette. Composite values
// (borders, shadows) live here because sx can't express them as palette paths.
export const brand = {
  terracotta: "#a8644f",
  terracottaDark: "#8f5340",
  terracottaSoft: "#d49a85",
  accent: "#E4B5A2",
  peach: "#FFB58A",
  band: "#FBF1EF",
  headline: "#2b2b2b",
  ink: "#333",
  body: "#555",
  muted: "#666",
  border: "1px solid rgba(228, 181, 162, 0.5)",
  borderSoft: "1px solid rgba(228, 181, 162, 0.35)",
  cardShadow: "0 2px 10px rgba(228, 181, 162, 0.25)",
};

const warmCardStyle = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  border: brand.border,
  boxShadow: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: brand.accent,
    boxShadow: brand.cardShadow,
  },
  "&:focus-visible": {
    outline: `3px solid ${brand.accent}`,
    outlineOffset: "3px",
  },
};

const theme = createTheme({
  palette: {
    primary: {
      main: brand.terracotta,
      dark: brand.terracottaDark,
      light: brand.terracottaSoft,
      contrastText: "#fff",
    },
    secondary: {
      main: brand.accent,
      light: brand.band,
      dark: brand.peach,
      contrastText: brand.ink,
    },
    peach: {
      main: brand.peach,
      contrastText: brand.ink,
    },
    headline: {
      main: brand.headline,
    },
    text: {
      primary: brand.ink,
      secondary: brand.body,
    },
  },
  typography: {
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem", // 40px - Accessible minimum, strong presence
      lineHeight: 1.2, // Tight for impact, readable
      marginBottom: "1.5rem", // Good breathing room
      "@media (min-width:600px)": {
        fontSize: "3rem", // 48px - Scales nicely for tablets
        marginBottom: "2rem",
      },
      "@media (min-width:900px)": {
        fontSize: "3.5rem", // 56px - Desktop hero size, not overwhelming
        marginBottom: "2rem",
      },
    },
  },
  components: {
    MuiCard: {
      variants: [
        {
          props: { variant: "warm" },
          style: warmCardStyle,
        },
      ],
    },
    MuiButton: {
      variants: [
        // Filled peach CTA (homepage "View all Services", consultation CTA)
        {
          props: { variant: "peach" },
          style: {
            backgroundColor: brand.accent,
            color: brand.ink,
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            boxShadow: "none",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: brand.peach,
              boxShadow: "none",
            },
          },
        },
        // Peach outlined (call/text buttons)
        {
          props: { variant: "peachOutlined" },
          style: {
            color: brand.ink,
            border: `1px solid ${brand.accent}`,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: brand.peach,
              borderColor: brand.peach,
              color: "#fff",
            },
          },
        },
        // White card-shaped button (Get in Touch contact options)
        {
          props: { variant: "warmCard" },
          style: {
            ...warmCardStyle,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#fff",
              borderColor: brand.accent,
              boxShadow: brand.cardShadow,
            },
          },
        },
      ],
    },
  },
});

export default theme;
